/**
 * Bordereau versioning schemas (Droit Public S3, 2026-06-20).
 *
 * Cross-app contract between :
 *   - apps/backend (routes/bordereau.routes.ts, BordereauService.createRecap)
 *   - apps/legalworkspace (admin TA flow, version chain UI)
 *
 * A bordereau may be issued multiple times during a procédure :
 *   - v1 = bordereau initial joint au recours
 *   - v2+ = productions complémentaires (additif) OU récapitulatif (cumulatif)
 *
 * `mode` drives the rendered PDF template, the ZIP filename convention
 * (Télérecours-safe for productions/recapitulatif), and the cover wording.
 */
import { z } from 'zod';

/**
 * Trois modes de bordereau procédural :
 *
 *   - 'communication' : bordereau classique (RPVA / judiciaire) — défaut historique
 *   - 'productions'   : productions complémentaires Télérecours (additif, daté)
 *   - 'recapitulatif' : récapitulatif Télérecours (cumulatif, numéroté n°N)
 */
export const BordereauModeSchema = z.enum([
  'communication',
  'productions',
  'recapitulatif',
]);
export type BordereauMode = z.infer<typeof BordereauModeSchema>;

/**
 * Payload du POST /api/bordereau/:id/new-version.
 *
 * `newDocIds`            : pièces ajoutées par rapport au parent (peut être []
 *                          pour un recap pur sans nouveauté, en pratique rare).
 * `communicationDate`    : date de communication aux parties (affichée dans
 *                          le titre « productions complémentaires du JJ/MM/AAAA »).
 * `mode`                 : 'productions' (additif) ou 'recapitulatif' (cumulatif).
 *                          'communication' n'est PAS autorisé ici — un bordereau
 *                          de communication = v1 créé via le flow classique.
 * `extraCaseIds`         : dossiers TA additionnels (consorts) qu'on couvre
 *                          dans le même bordereau (Conseil d'État accepte les
 *                          recours joints).
 */
// `communicationDate` accepte DEUX formes — la colonne PG est `date` (date-only),
// le frontend pose un `<input type="date">` qui produit `YYYY-MM-DD`, mais un appel
// scripté peut envoyer un ISO datetime complet. Le regex couvre les deux.
// Bug rattrapé au review 2026-06-20 : `z.string().datetime()` strict rejetait
// `'2026-06-20'` → 100 % des génération Télérecours échouait runtime.
const ISO_DATE_OR_DATETIME = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:?\d{2})?)?$/;

export const CreateBordereauVersionPayloadSchema = z.object({
  newDocIds: z.array(z.string().uuid()),
  communicationDate: z.string().regex(ISO_DATE_OR_DATETIME, {
    message: 'communicationDate must be YYYY-MM-DD or full ISO 8601 datetime',
  }),
  mode: z.enum(['productions', 'recapitulatif']),
  extraCaseIds: z.array(z.string().uuid()).optional(),
});
export type CreateBordereauVersionPayload = z.infer<
  typeof CreateBordereauVersionPayloadSchema
>;

/**
 * Une ligne de la chaîne de versions (GET /api/bordereau/case/:caseId/versions).
 * Les champs JSON natifs DB (`metadata`) restent passthrough côté API.
 */
export const BordereauVersionRowSchema = z
  .object({
    id: z.string().uuid(),
    case_id: z.string().uuid(),
    cabinet_id: z.string().uuid(),
    name: z.string(),
    status: z.string(),
    mode: BordereauModeSchema,
    version_number: z.number().int().positive(),
    parent_bordereau_id: z.string().uuid().nullable(),
    communication_date: z.string().nullable(),
    extra_case_ids: z.array(z.string().uuid()).nullable(),
    pieces_count: z.number().int().nonnegative(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough();
export type BordereauVersionRow = z.infer<typeof BordereauVersionRowSchema>;
